// store/useAppStore.ts - Zustand store for UI and workflow state
// Note: Posts and Media data are now managed by React Query (see usePostsQuery.ts, useMediaQuery.ts)
import { create } from "zustand";
import { devtools, persist, subscribeWithSelector } from "zustand/middleware";
import { UserSlice, createUserSlice } from "./slices/userSlice";
import { ClientSlice, createClientSlice } from "./slices/clientSlice";
import { GridSlice, createGridSlice } from "./slices/gridSlice";
import { UISlice, createUISlice } from "./slices/uiSlice";
import { CaptionSlice, createCaptionSlice } from "./slices/captionSlice";
import { RealtimeSlice, createRealtimeSlice } from "./slices/realtimeSlice";
import { CollectionsSlice, createCollectionsSlice } from "./slices/collectionsSlice";

// Store now contains only UI state and workflow state
// Posts and Media Items are managed by React Query for better caching and SSE updates
type AppStore = UserSlice & ClientSlice & GridSlice & UISlice & CaptionSlice & RealtimeSlice & CollectionsSlice;

export const useAppStore = create<AppStore>()(
  devtools(
    subscribeWithSelector(
      persist(
        (...a) => ({
          ...createUserSlice(...a),
          ...createClientSlice(...a),
          ...createGridSlice(...a),
          ...createUISlice(...a),
          ...createCaptionSlice(...a),
          ...createRealtimeSlice(...a),
          ...createCollectionsSlice(...a),
        }),
        {
          name: "app-storage",
          partialize: (state) => ({
            user: state.user,
            selectedClient: state.selectedClient,
            captions: state.captions,
            currentGridMode: state.currentGridMode,
          }),
          onRehydrateStorage: () => (state) => {
            // Migrate from legacy "user" key if app-storage has no user
            if (!state?.user) {
              const legacyUser = localStorage.getItem("user");
              if (legacyUser) {
                try {
                  const parsed = JSON.parse(legacyUser);
                  state?.setUser(parsed);
                } catch {
                  // Invalid JSON, ignore
                }
              }
            }
            state?.setHasHydrated(true);
          },
        },
      ),
    ),
  ),
);

// Sync user changes to legacy "user" key for backwards compatibility
// This ensures code that reads from localStorage.getItem("user") still works
useAppStore.subscribe(
  (state) => state.user,
  (user) => {
    if (user) {
      localStorage.setItem("user", JSON.stringify(user));
    } else {
      localStorage.removeItem("user");
    }
  },
);
